/**
 * Escáner del QR del centro de trabajo.
 *
 * El QR es dinámico: rota cada 30 s para que una foto pegada en el vestuario
 * no sirva para fichar desde el sofá. Por eso aquí hay una cuenta atrás y un
 * estado explícito de "caducado": si falla en silencio, el trabajador cree
 * que el escáner está roto.
 *
 * Se decodifica en local con jsQR sobre un canvas. El vídeo nunca sale del
 * dispositivo: no subimos imágenes a ningún sitio.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { Icon } from './ui/Icon';

const QR_TTL_SECONDS = 30;
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

interface QrScannerProps {
  onScan: (worksiteUuid: string, qrToken?: string) => void;
  onCancel: () => void;
}

type CameraState = 'starting' | 'live' | 'denied' | 'unavailable';

/** El QR puede llevar el UUID pelado o una URL que lo contiene. */
/** El QR de pantalla codifica JSON {project_uuid, token, ...}; el impreso, solo el UUID. */
function extractToken(payload: string): string | undefined {
  try {
    const parsed = JSON.parse(payload) as { token?: string };
    return typeof parsed.token === 'string' ? parsed.token : undefined;
  } catch {
    return undefined;
  }
}

function extractUuid(payload: string): string | null {
  const match = payload.match(UUID_RE);
  return match ? match[0].toLowerCase() : null;
}

export function QrScanner({ onScan, onCancel }: QrScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number>();
  const doneRef = useRef(false);

  // `onScan` vive en un ref "latest": el efecto de cámara NO puede depender
  // de él. PunchScreen re-renderiza cada ~30 s (poll de GPS + estado del
  // sync), cada re-render regeneraba la closure inline y el efecto se
  // desmontaba y remontaba: la cámara se APAGABA y volvía a encenderse con
  // el escáner abierto, cortando el vídeo y perdiendo frames justo cuando
  // el trabajador estaba encuadrando el QR (ronda 4, r2b18[6]).
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  const [camera, setCamera] = useState<CameraState>('starting');
  const [seconds, setSeconds] = useState(QR_TTL_SECONDS);
  const [expired, setExpired] = useState(false);
  const [badPayload, setBadPayload] = useState(false);

  const stop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const restart = useCallback(() => {
    doneRef.current = false;
    setExpired(false);
    setBadPayload(false);
    setSeconds(QR_TTL_SECONDS);
  }, []);

  // Cámara + bucle de lectura
  useEffect(() => {
    let cancelled = false;

    async function start() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setCamera('unavailable');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setCamera('live');
        tick();
      } catch {
        if (!cancelled) setCamera('denied');
      }
    }

    function tick() {
      rafRef.current = requestAnimationFrame(tick);

      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) return;
      if (doneRef.current) return;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(image.data, image.width, image.height, {
        inversionAttempts: 'dontInvert',
      });
      if (!code?.data) return;

      const uuid = extractUuid(code.data);
      if (!uuid) {
        // Un QR cualquiera (una etiqueta, un cartel). No es un error del
        // trabajador: se lo decimos y seguimos escaneando.
        setBadPayload(true);
        return;
      }

      doneRef.current = true;
      if ('vibrate' in navigator) navigator.vibrate(60);
      stop();
      onScanRef.current(uuid, extractToken(code.data));
    }

    start();
    return () => {
      cancelled = true;
      stop();
    };
  }, [stop]);

  // Cuenta atrás del QR dinámico
  useEffect(() => {
    if (expired || camera !== 'live') return;
    const id = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          setExpired(true);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [expired, camera, seconds === QR_TTL_SECONDS]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#0A0B06] text-[#F3F0E8]">
      <header className="flex items-center justify-between px-4 pt-4 pb-2 safe-area-top">
        <button
          onClick={() => {
            stop();
            onCancel();
          }}
          aria-label="Cancelar"
          className="w-10 h-10 rounded-full grid place-items-center bg-white/10"
        >
          <Icon name="chevron-left" size={20} strokeWidth={2} />
        </button>
        <span className="font-semibold text-[15px]">Escanea el QR de tu centro</span>
        <span className="w-10" />
      </header>

      <div className="flex-1 relative grid place-items-center overflow-hidden">
        <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover" />
        <canvas ref={canvasRef} className="hidden" />

        {/* Marco de encuadre */}
        <div className="relative w-[250px] h-[250px]">
          {!expired && camera === 'live' && (
            <div className="absolute left-0 right-0 h-0.5 bg-brand shadow-[0_0_14px_var(--brand)] animate-scan" />
          )}
          <span className="absolute top-0 left-0 w-10 h-10 border-t-4 border-l-4 border-[#F3F0E8] rounded-tl-md" />
          <span className="absolute top-0 right-0 w-10 h-10 border-t-4 border-r-4 border-[#F3F0E8] rounded-tr-md" />
          <span className="absolute bottom-0 left-0 w-10 h-10 border-b-4 border-l-4 border-[#F3F0E8] rounded-bl-md" />
          <span className="absolute bottom-0 right-0 w-10 h-10 border-b-4 border-r-4 border-[#F3F0E8] rounded-br-md" />
        </div>

        {/* ⚠️ Esta cuenta atrás NO sabe cuándo emitió el servidor el QR que hay
            en la pantalla: empieza cuando se abre el escáner. Decía "este QR ha
            caducado" de un código perfectamente vigente (tardaste 31 s en
            enfocar) y dejaba pasar uno a punto de rotar, que el servidor
            rechazaba sin explicación. La autoridad es el backend; esto es un
            recordatorio, y así se dice. */}
        {expired && (
          <div className="absolute inset-0 bg-[#0A0B06]/90 flex flex-col items-center justify-center gap-4 px-8 text-center">
            <Icon name="clock" size={48} className="text-signal" />
            <div>
              <p className="font-display font-extrabold text-[22px]">
                Comprueba el código de la pantalla
              </p>
              <p className="text-[#c9c6b8] text-[14.5px] mt-1.5 max-w-[26ch] mx-auto">
                Cambia cada 30 s para evitar fotos. Si el de la pantalla ya no es
                el que ves aquí, vuelve a escanear.
              </p>
            </div>
            <button
              onClick={restart}
              className="h-13 px-7 py-3 rounded-xl bg-brand text-on-brand font-bold text-[16px]"
            >
              Volver a escanear
            </button>
          </div>
        )}

        {(camera === 'denied' || camera === 'unavailable') && (
          <div className="absolute inset-0 bg-[#0A0B06]/95 flex flex-col items-center justify-center gap-4 px-8 text-center">
            <Icon name="camera" size={48} className="text-signal" />
            <div>
              <p className="font-display font-extrabold text-[22px]">
                {camera === 'denied' ? 'Sin permiso de cámara' : 'Este dispositivo no tiene cámara'}
              </p>
              <p className="text-[#c9c6b8] text-[14.5px] mt-1.5 max-w-[30ch] mx-auto">
                {camera === 'denied'
                  ? 'Actívala en los ajustes del navegador, o ficha desde el kiosco del centro.'
                  : 'Ficha desde el kiosco del centro.'}
              </p>
            </div>
            <button
              onClick={() => {
                stop();
                onCancel();
              }}
              className="h-12 px-6 rounded-xl border border-white/30 font-semibold text-[15px]"
            >
              Volver
            </button>
          </div>
        )}
      </div>

      <div className="px-6 pt-4 pb-7 text-center safe-area-bottom">
        {badPayload && !expired && (
          <p className="text-signal text-[14px] mb-3">
            Ese código no es de un centro de Kakuat. Busca el QR del tablón.
          </p>
        )}
        {camera === 'live' && !expired && (
          <p className="inline-flex items-center gap-2 font-mono text-[13px] text-[#c9c6b8]">
            <span className="w-2 h-2 rounded-full bg-brand animate-blink" />
            QR válido durante {seconds} s
          </p>
        )}
      </div>
    </div>
  );
}
